import React, {createElement} from 'react';
import styled from 'styled-components';

import {
  annotationHtmlAttribute,
  annotationLinkedTypeAttribute,
  skipTextContentClass,
  sourceActivityIdAttribute,
} from '../../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  CommentAnnotationModel,
  maybeFindAnnotationLinkedActivityId,
} from '../../../../../libs/shared/core/src/models/api/annotationModel';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  MarkdownAstToReactComponentsMap,
  OrderedListBulletTypesEnum,
  UnorderedListBulletTypesEnum,
} from '../../../../../srcCommon/formatters/markdown/markdownAstToReact';

const StyledSkipTextContentIndicesComponentWrapper = styled.div`
  display: inline;
`;

const StyledErrorSpan = styled.span`
  color: ${(p) => p.theme.palette.red.shade50};
  font-weight: ${fontWeights.semibold};
  font-style: italic;
`;

export const markdownDomComponents: MarkdownAstToReactComponentsMap<CommentAnnotationModel> = Object.freeze({
  ErrorFallbackComponent: () => (
    <StyledErrorSpan className={skipTextContentClass}>
      <Trans id="markdown-error-fallback">An error occurred, this text cannot be displayed.</Trans>
    </StyledErrorSpan>
  ),
  LineBreak: <br />,
  Paragraph: ({children}) => <div className="paragraph">{children}</div>,
  Blockquote: ({children}) => <blockquote>{children}</blockquote>,
  UnorderedList: ({children, listStyle}) => <ul style={unorderedListStyle(listStyle)}>{children}</ul>,
  OrderedList: ({children, listStyle, start}) => (
    <ol start={start} style={orderedListStyle(listStyle)}>
      {children}
    </ol>
  ),
  ListItem: ({children}) => <li>{children}</li>,
  Code: ({children}) => (
    <pre>
      <code>{children}</code>
    </pre>
  ),
  InlineCode: ({children}) => <code>{children}</code>,
  Emphasis: ({children}) => <em>{children}</em>,
  Strong: ({children}) => <strong>{children}</strong>,
  Delete: ({children}) => <s>{children}</s>,
  Link: ({children, href, title}) => (
    <a href={href} title={title}>
      {children}
    </a>
  ),
  Annotation: ({children, annotation}) => {
    const attributes = {
      class: 'front_annotation',
      'data-markjs': 'true',
      [annotationLinkedTypeAttribute]: annotation.linkedResource.type,
      [annotationHtmlAttribute]: annotation.clientSideUid,
      [sourceActivityIdAttribute]: maybeBuildAnnotationDataComment(annotation),
    };
    return createElement('front-annotation', attributes, children);
  },
  SkipTextContentIndicesComponentWrapper: ({children}) => (
    <StyledSkipTextContentIndicesComponentWrapper className={skipTextContentClass}>
      {children}
    </StyledSkipTextContentIndicesComponentWrapper>
  ),
});

function maybeBuildAnnotationDataComment(annotation: CommentAnnotationModel) {
  // Comment drafts can have annotations, but don't have an activity yet.
  const annotationCommentActivityId = maybeFindAnnotationLinkedActivityId(annotation);
  if (!annotationCommentActivityId) {
    return '';
  }
  // This is used to jump to the activity when clicking on the annotation.
  return String(annotationCommentActivityId);
}

function orderedListStyle(type: OrderedListBulletTypesEnum): React.CSSProperties {
  switch (type) {
    case OrderedListBulletTypesEnum.DECIMAL:
      return {listStyleType: 'decimal'};
    case OrderedListBulletTypesEnum.LOWER_ALPHA:
      return {listStyleType: 'lower-alpha'};
    case OrderedListBulletTypesEnum.LOWER_ROMAN:
      return {listStyleType: 'lower-roman'};
    default:
      throw new UnreachableError(type);
  }
}

function unorderedListStyle(type: UnorderedListBulletTypesEnum): React.CSSProperties {
  switch (type) {
    case UnorderedListBulletTypesEnum.DISC:
      return {listStyleType: 'disc'};
    case UnorderedListBulletTypesEnum.CIRCLE:
      return {listStyleType: 'circle'};
    case UnorderedListBulletTypesEnum.SQUARE:
      return {listStyleType: 'square'};
    default:
      throw new UnreachableError(type);
  }
}
